import React from 'react';

interface LogoProps {
  variant?: 'full' | 'icon';
  color?: 'full-color' | 'blue' | 'black' | 'white';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

/**
 * PermitBuddy Logo Component
 *
 * Displays the PermitBuddy logo with support for different variants, colors, and sizes.
 *
 * @param variant - 'full' for icon + wordmark, 'icon' for icon only (default: 'full')
 * @param color - Color variant: 'full-color', 'blue', 'black', 'white' (default: 'full-color')
 * @param size - Size preset: 'sm', 'md', 'lg', 'xl' (default: 'md')
 * @param className - Additional CSS classes
 */
const Logo: React.FC<LogoProps> = ({
  variant = 'full',
  color = 'full-color',
  size = 'md',
  className = '',
}) => {
  // Size mappings for different logo variants
  const sizeClasses = {
    full: {
      sm: 'h-6',     // 24px height
      md: 'h-8',     // 32px height
      lg: 'h-10',    // 40px height
      xl: 'h-12',    // 48px height
    },
    icon: {
      sm: 'h-6 w-6',   // 24x24px
      md: 'h-8 w-8',   // 32x32px
      lg: 'h-10 w-10', // 40x40px
      xl: 'h-12 w-12', // 48x48px
    },
  };

  // Construct logo path based on variant and color
  const logoPath = variant === 'full'
    ? `/logos/full/logo-${color}.svg`
    : `/logos/icon/icon-${color}.svg`;

  // Get appropriate size class for the variant
  const sizeClass = sizeClasses[variant][size];

  return (
    <img
      src={logoPath}
      alt="PermitBuddy"
      className={`${sizeClass} ${className}`.trim()}
      loading="eager" // Logo is above-the-fold, load immediately
    />
  );
};

export default Logo;
